import {
  Button,
  ButtonCustomContent,
  ButtonProps,
  ConditionalWrapper,
  Flex,
  Icon,
  Text,
} from '@fullstory/components';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import css from './pill.module.scss';
import { PlayButtonProps } from './PlayButton';
import { classNames } from '@fullstory/util/fmt';
import { intl } from '@fullstory/intl';

export interface PlayButtonPillProps extends PlayButtonProps {
  sessionLength: string;
  isServer?: boolean;
  newUi?: boolean;
}

export const PlayButtonPill: React.FC<PlayButtonPillProps> = ({
  to,
  isOnline,
  isWatched,
  isClip,
  isServer,
  onClick,
  newTab = false,
  disabled = false,
  testId,
  sessionLength,
  newUi = false,
}) => {
  const buttonVariant: ButtonProps['variant'] = useMemo(() => {
    if (isWatched) {
      return 'fill-builder';
    }

    if (isOnline) {
      return 'fill-primary';
    }

    return 'fill-secondary';
  }, [isOnline, isWatched]);

  const iconName: React.ComponentProps<typeof Button>['iconLeft'] = useMemo(() => {
    if (newUi) {
      if (isServer) {
        return 'server';
      }
      if (isClip) {
        return 'clip';
      }

      return 'play';
    } else {
      if (isServer) {
        return 'server';
      }

      return 'play';
    }
  }, [isClip, isServer, newUi]);

  const label = useMemo(() => {
    if (newUi) {
      if (isServer) {
        return intl.formatMessage({ defaultMessage: 'Server', id: 'F/6VqP' });
      }
      if (isOnline) {
        return intl.formatMessage({ defaultMessage: 'Live...', id: 'p1qEuM' });
      }
      if (isClip) {
        return intl.formatMessage({ defaultMessage: 'Clip', id: 'yj8NrY' });
      }

      return sessionLength;
    } else {
      if (isServer) {
        return 'Server';
      }

      if (isClip) {
        return 'Clip';
      }

      return sessionLength;
    }
  }, [newUi, isServer, isOnline, isClip, sessionLength]);

  if (isServer && !newUi) {
    return (
      <Button iconLeft={iconName} size="x-small" variant="fill-secondary" disabled>
        {label}
      </Button>
    );
  }

  if (newUi) {
    return (
      <ConditionalWrapper
        data-component="PlayButtonPillNew"
        condition={!disabled}
        wrapper={children => (
          <Link
            data-component="PlayButtonPill"
            to={to}
            data-testid={testId || 'play-button'}
            target={newTab ? '_blank' : '_self'}
            data-is-clip={isClip}
            data-is-online={isOnline}
            data-is-watched={isWatched}
            color="inherit"
            data-fs-properties-schema='{"href":"str","data-is-clip":"bool","data-is-online":"bool","data-is-watched":"bool"}'
          >
            {children}
          </Link>
        )}
      >
        <ButtonCustomContent
          onClick={onClick}
          size="medium"
          variant={buttonVariant}
          disabled={disabled}
          className={classNames(css.playButton, {
            [css.live]: !isClip && isOnline && !disabled && !isWatched,
            [css.notLive]: !isClip && !isOnline && !disabled && !isWatched,
            [css.clip]: isClip && !disabled,
            [css.watched]: isWatched,
            [css.watchedButton]: isWatched,
            [css.newUI]: true,
          })}
          style={{
            padding: '12px 8px',
            height: '40px',
          }}
        >
          <Flex
            gap={4}
            alignItems="center"
            style={{
              height: '48px',
            }}
          >
            <div style={{ marginRight: '0px' }}>
              <Icon name={iconName} size={16} />
            </div>
            {label && (
              <Text
                appearance="bodySmallBold"
                noWrap
                className={classNames({
                  [css.live]: !isClip && isOnline && !disabled && !isWatched,
                  [css.notLive]: !isClip && !isOnline && !disabled && !isWatched,
                  [css.clip]: isClip && !disabled,
                  [css.watched]: isWatched,
                  [css.newUI]: true,
                })}
              >
                {label}
              </Text>
            )}
          </Flex>
        </ButtonCustomContent>
      </ConditionalWrapper>
    );
  }

  // NB: When this is removed, a lot of CSS rules can be removed from pill.module.scss as well.
  return (
    <Link
      data-component="PlayButtonPill"
      to={to}
      data-testid={testId || 'play-button'}
      target={newTab ? '_blank' : '_self'}
      data-is-clip={isClip}
      data-is-online={isOnline}
      data-is-watched={isWatched}
      color="inherit"
      data-fs-properties-schema='{"href":"str","data-is-clip":"bool","data-is-online":"bool","data-is-watched":"bool"}'
    >
      <Button
        onClick={onClick}
        iconLeft={iconName}
        size="x-small"
        variant={buttonVariant}
        disabled={disabled}
      >
        {label && (
          <span
            className={classNames(css.label, {
              [css.labelBlack]: buttonVariant === 'fill-secondary' && !isClip,
            })}
          >
            {label}
          </span>
        )}
      </Button>
    </Link>
  );
};

PlayButtonPill.displayName = 'PlayButtonPill';
